import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  X,
  Search,
  Image,
  Video,
  Trash2,
  Check,
  Loader2,
  FolderOpen,
  AlertCircle,
  Download,
  Copy,
  ChevronDown,
  Plus,
} from 'lucide-react';
import { uploadApi } from '../api/client';
import type { MediaItem } from '../api/modules/upload';
import { DeleteConfirmModal } from './DeleteConfirmModal';

interface MediaLibraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteId?: string;
  selectionMode?: boolean;
  mediaTypeFilter?: 'image' | 'video' | 'all';
  onSelect?: (media: MediaItem) => void;
}

/**
 * Format bytes to human-readable string
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Format date to relative time string
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

/**
 * Check if media item is an image based on mime type
 */
const isImage = (mimeType: string): boolean => mimeType.startsWith('image/');

/**
 * Check if media item is a video based on mime type
 */
const isVideo = (mimeType: string): boolean => mimeType.startsWith('video/');

export const MediaLibraryModal: React.FC<MediaLibraryModalProps> = ({
  isOpen,
  onClose,
  siteId,
  selectionMode = false,
  mediaTypeFilter = 'all',
  onSelect,
}) => {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'image' | 'video'>(
    mediaTypeFilter === 'all' ? 'all' : mediaTypeFilter
  );
  const [siteFilter, setSiteFilter] = useState<'all' | 'current'>(siteId ? 'current' : 'all');
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [previewMedia, setPreviewMedia] = useState<MediaItem | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [deleteConfirmMedia, setDeleteConfirmMedia] = useState<MediaItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch media on mount and when filters change
  useEffect(() => {
    if (!isOpen) return;

    const fetchMedia = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const filterSiteId = siteFilter === 'current' ? siteId : undefined;
        const data = await uploadApi.getMedia(filterSiteId);
        setMedia(data);
      } catch (err) {
        console.error('Failed to fetch media:', err);
        setError('Failed to load media library. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMedia();
  }, [isOpen, siteFilter, siteId]);

  // Filter media based on search and type
  const filteredMedia = useMemo(() => {
    return media.filter(item => {
      // Type filter
      if (typeFilter === 'image' && !isImage(item.mimeType)) return false;
      if (typeFilter === 'video' && !isVideo(item.mimeType)) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return item.filename.toLowerCase().includes(query);
      }

      return true;
    });
  }, [media, typeFilter, searchQuery]);

  // Calculate total size
  const totalSize = useMemo(() => {
    return filteredMedia.reduce((acc, item) => acc + item.size, 0);
  }, [filteredMedia]);

  // Handle delete - opens confirmation modal
  const handleDelete = (mediaItem: MediaItem) => {
    setDeleteError(null);
    setDeleteConfirmMedia(mediaItem);
  };

  // Confirm delete - actually performs the deletion
  const handleConfirmDelete = async () => {
    if (!deleteConfirmMedia) return;

    try {
      setDeletingId(deleteConfirmMedia.id);
      await uploadApi.deleteMedia(deleteConfirmMedia.id);
      setMedia(prev => prev.filter(m => m.id !== deleteConfirmMedia.id));
      if (selectedMedia?.id === deleteConfirmMedia.id) {
        setSelectedMedia(null);
      }
      // Close preview modal if deleting the previewed item
      if (previewMedia?.id === deleteConfirmMedia.id) {
        setPreviewMedia(null);
      }
      setDeleteConfirmMedia(null);
    } catch (err) {
      console.error('Failed to delete media:', err);
      setDeleteError('Failed to delete media. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  // Handle selection or preview
  const handleSelect = (mediaItem: MediaItem) => {
    if (selectionMode) {
      setSelectedMedia(mediaItem);
    } else {
      // Open preview in browse mode
      setPreviewMedia(mediaItem);
    }
  };

  // Copy URL to clipboard
  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  // Download media
  const handleDownload = (mediaItem: MediaItem) => {
    const url = mediaItem.cloudfrontUrl || mediaItem.url;
    const link = document.createElement('a');
    link.href = url;
    link.download = mediaItem.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle use selected
  const handleUseSelected = () => {
    if (selectedMedia && onSelect) {
      onSelect(selectedMedia);
      onClose();
    }
  };

  // Handle file upload
  const handleFileUpload = async (file: File) => {
    // Validate file type based on filter
    const isImageFile = file.type.startsWith('image/');
    const isVideoFile = file.type.startsWith('video/');

    if (mediaTypeFilter === 'image' && !isImageFile) {
      setUploadError('Please select an image file');
      return;
    }
    if (mediaTypeFilter === 'video' && !isVideoFile) {
      setUploadError('Please select a video file');
      return;
    }
    if (!isImageFile && !isVideoFile) {
      setUploadError('Please select an image or video file');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      const response = await uploadApi.uploadWithPresignedUrl(file, {
        siteId,
        onProgress: progress => setUploadProgress(progress),
      });

      // Add the new media item to the list
      const newMediaItem: MediaItem = {
        id: response.id,
        url: response.url,
        filename: response.filename,
        size: response.size,
        mimeType: response.mimeType,
        s3Key: response.s3Key,
        cloudfrontUrl: response.cloudfrontUrl,
        createdAt: response.createdAt,
      };

      setMedia(prev => [newMediaItem, ...prev]);
      setUploadProgress(100);

      // Reset after brief delay to show completion
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setUploadError(err.message || 'Failed to upload file');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[70]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl border border-gray-100 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <FolderOpen size={24} className="text-primary-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {selectionMode
                  ? `Select ${mediaTypeFilter === 'video' ? 'Video' : 'Image'}`
                  : 'Media Library'}
              </h2>
              <p className="text-sm text-gray-500">
                {selectionMode
                  ? 'Choose from your uploaded media'
                  : 'Browse and manage your uploaded media'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Hidden file input for upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept={
            mediaTypeFilter === 'image'
              ? 'image/*'
              : mediaTypeFilter === 'video'
                ? 'video/*'
                : 'image/*,video/*'
          }
          onChange={handleFileInputChange}
          className="hidden"
        />

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div className="flex flex-wrap items-center gap-4">
            {/* Upload Button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>{uploadProgress}%</span>
                </>
              ) : (
                <>
                  <Plus size={16} />
                  <span>Upload</span>
                </>
              )}
            </button>

            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by filename..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Type Filter */}
            {mediaTypeFilter === 'all' && (
              <div className="relative">
                <select
                  value={typeFilter}
                  onChange={e => setTypeFilter(e.target.value as 'all' | 'image' | 'video')}
                  className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 bg-white cursor-pointer"
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    backgroundImage: 'none',
                  }}
                >
                  <option value="all">All Types</option>
                  <option value="image">Images</option>
                  <option value="video">Videos</option>
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            )}

            {/* Site Filter */}
            {siteId && (
              <div className="relative">
                <select
                  value={siteFilter}
                  onChange={e => setSiteFilter(e.target.value as 'all' | 'current')}
                  className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 bg-white cursor-pointer"
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    backgroundImage: 'none',
                  }}
                >
                  <option value="current">This Site</option>
                  <option value="all">All Sites</option>
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Upload Error */}
          {uploadError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle size={16} />
                <span>{uploadError}</span>
              </div>
              <button
                onClick={() => setUploadError(null)}
                className="text-red-500 hover:text-red-700"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 size={32} className="text-primary-600 animate-spin mb-4" />
              <p className="text-gray-500">Loading media library...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle size={32} className="text-red-500 mb-4" />
              <p className="text-gray-700 font-medium mb-2">Error</p>
              <p className="text-gray-500 text-sm">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 text-sm text-primary-600 hover:text-primary-700"
              >
                Try Again
              </button>
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <FolderOpen size={48} className="text-gray-300 mb-4" />
              <p className="text-gray-700 font-medium mb-2">No media found</p>
              <p className="text-gray-500 text-sm">
                {searchQuery ? 'Try a different search term' : 'Upload some media to get started'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredMedia.map(item => {
                const selected = selectedMedia?.id === item.id;
                const deleting = deletingId === item.id;
                const itemIsImage = isImage(item.mimeType);

                return (
                  <div
                    key={item.id}
                    className={`group relative rounded-lg border-2 overflow-hidden transition-all cursor-pointer ${
                      selected
                        ? 'border-primary-500 ring-2 ring-primary-200'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${deleting ? 'opacity-50' : ''}`}
                    onClick={() => handleSelect(item)}
                  >
                    {/* Thumbnail */}
                    <div className="aspect-square bg-gray-100 relative">
                      {itemIsImage ? (
                        <img
                          src={item.cloudfrontUrl || item.url}
                          alt={item.filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                          <Video size={32} className="text-white" />
                        </div>
                      )}

                      {/* Selection indicator */}
                      {selectionMode && selected && (
                        <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                            <Check size={18} className="text-white" />
                          </div>
                        </div>
                      )}

                      {/* Type badge */}
                      <div className="absolute top-2 left-2">
                        {itemIsImage ? (
                          <Image size={14} className="text-white drop-shadow-lg" />
                        ) : (
                          <Video size={14} className="text-white drop-shadow-lg" />
                        )}
                      </div>

                      {/* Delete button (non-selection mode) */}
                      {!selectionMode && (
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleDelete(item);
                          }}
                          disabled={deleting}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                          title="Delete"
                        >
                          {deleting ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Trash2 size={12} />
                          )}
                        </button>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-2">
                      <p
                        className="text-xs font-medium text-gray-700 truncate"
                        title={item.filename}
                      >
                        {item.filename}
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-400">{formatFileSize(item.size)}</span>
                        <span className="text-xs text-gray-400">
                          {formatRelativeTime(item.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <div className="text-sm text-gray-500">
            {filteredMedia.length} {filteredMedia.length === 1 ? 'item' : 'items'}
            {totalSize > 0 && ` • ${formatFileSize(totalSize)} total`}
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={onClose} className="btn-secondary">
              {selectionMode ? 'Cancel' : 'Close'}
            </button>
            {selectionMode && (
              <button
                onClick={handleUseSelected}
                disabled={!selectedMedia}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Use Selected
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {previewMedia && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60]"
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Preview Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <div className="flex items-center space-x-3 min-w-0">
                {isImage(previewMedia.mimeType) ? (
                  <Image size={20} className="text-primary-600 flex-shrink-0" />
                ) : (
                  <Video size={20} className="text-primary-600 flex-shrink-0" />
                )}
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {previewMedia.filename}
                </h3>
              </div>
              <button
                onClick={() => setPreviewMedia(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center p-4">
              {isImage(previewMedia.mimeType) ? (
                <img
                  src={previewMedia.cloudfrontUrl || previewMedia.url}
                  alt={previewMedia.filename}
                  className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                />
              ) : (
                <video
                  src={previewMedia.cloudfrontUrl || previewMedia.url}
                  controls
                  className="max-w-full max-h-[60vh] rounded-lg shadow-lg"
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>

            {/* Preview Details */}
            <div className="p-4 border-t border-gray-100 bg-white">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Size</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatFileSize(previewMedia.size)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Type</p>
                  <p className="text-sm font-medium text-gray-900">{previewMedia.mimeType}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Uploaded</p>
                  <p className="text-sm font-medium text-gray-900">
                    {formatRelativeTime(previewMedia.createdAt)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">ID</p>
                  <p className="text-sm font-medium text-gray-900 truncate" title={previewMedia.id}>
                    {previewMedia.id.slice(0, 8)}...
                  </p>
                </div>
              </div>

              {/* URL Display */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">URL</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    readOnly
                    value={previewMedia.cloudfrontUrl || previewMedia.url}
                    className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-600"
                  />
                  <button
                    onClick={() => handleCopyUrl(previewMedia.cloudfrontUrl || previewMedia.url)}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center space-x-1"
                  >
                    <Copy size={14} />
                    <span>{copiedUrl ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => handleDelete(previewMedia)}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleDownload(previewMedia)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Download size={16} />
                    <span>Download</span>
                  </button>
                  <button
                    onClick={() => setPreviewMedia(null)}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteConfirmMedia}
        onClose={() => {
          setDeleteConfirmMedia(null);
          setDeleteError(null);
        }}
        onConfirm={handleConfirmDelete}
        isLoading={!!deletingId}
        title={`Delete "${deleteConfirmMedia?.filename || 'file'}"?`}
        message="If this file is used in any components on your site, those components will display a broken image or video. Consider checking your pages before deleting."
        confirmButtonText="Delete"
        errorMessage={deleteError}
      />
    </div>
  );
};
